import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  IconButton, CircularProgress, Button, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { cardManagementService } from '../../../services/cardManagementService';
import { useTranslation } from 'react-i18next';

interface PurchaseItemsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  mode: 'transaction' | 'session';
  referenceId?: string | null;
  sessionId?: number | null;
  title?: string;
}

interface PurchaseItem {
  productId: string;
  productName: string;
  totalQuantity: number;
  unit: string;
  unitPrice: number;
  totalAmount: number;
  vatRate: number;
  purchaseCount?: number;
  // Oturum modunda her satın alma ayrı satırdır (backend satın alma bazlı döner)
  referenceId?: string | null;
  purchasedAt?: string | null;
}

const formatPurchasedAt = (value?: string | null) => {
  if (!value) return null;
  const iso = value.endsWith('Z') ? value : `${value}Z`;
  return new Date(iso).toLocaleString('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatCurrency = (amount: number) =>
  amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₺';

const PurchaseItemsModal: React.FC<PurchaseItemsModalProps> = ({
  open, onClose, companyId, mode, referenceId, sessionId, title,
}) => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [grandTotal, setGrandTotal] = useState(0);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      if (mode === 'transaction' && referenceId) {
        const res = await cardManagementService.getStockMovementsByReference(referenceId, companyId);
        const movements = res.data?.data || [];
        const grouped = groupMovements(movements);
        setItems(grouped);
        setGrandTotal(grouped.reduce((sum: number, i: PurchaseItem) => sum + i.totalAmount, 0));
      } else if (mode === 'session' && sessionId) {
        const res = await cardManagementService.getSessionPurchaseItems(sessionId, companyId);
        setItems(res.data?.data || []);
        setGrandTotal(res.data?.grandTotal || 0);
      }
    } catch (err) {
      console.error('Purchase item details failed to load:', err);
      setItems([]);
    }
    setLoading(false);
  }, [mode, referenceId, sessionId, companyId]);

  const groupMovements = (movements: any[]): PurchaseItem[] => {
    const filtered = movements.filter(m =>
      !m.referenceType || m.referenceType === 'CARD_PURCHASE'
    );
    const map = new Map<string, PurchaseItem>();
    for (const m of filtered) {
      const key = m.productId;
      const existing = map.get(key);
      if (existing) {
        existing.totalQuantity += m.quantity || 0;
        existing.totalAmount += m.totalAmount || 0;
      } else {
        map.set(key, {
          productId: m.productId,
          productName: m.productName || cm('purchaseItems.product', 'Product'),
          totalQuantity: m.quantity || 0,
          unit: m.unit || cm('purchaseItems.piece', 'Piece'),
          unitPrice: m.unitPrice || 0,
          totalAmount: m.totalAmount || 0,
          vatRate: m.vatRate || 0,
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.productName.localeCompare(b.productName, 'tr'));
  };

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  const modalTitle = title || (mode === 'session' ? cm('purchaseItems.sessionTitle', 'Products Purchased In Session') : cm('purchaseItems.title', 'Purchased Products'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {mode === 'session' ? <ShoppingCartIcon color="primary" /> : <ReceiptLongIcon color="secondary" />}
          <Typography variant="h6" component="span">{modalTitle}</Typography>
          {!loading && (
            <Chip label={`${items.length} ${cm('purchaseItems.products', 'products')}`} size="small" variant="outlined" />
          )}
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <Typography color="text.secondary">{cm('purchaseItems.noProducts', 'No products found')}</Typography>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold', width: 50 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{cm('purchaseItems.columns.productName', 'Product Name')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{cm('purchaseItems.columns.quantity', 'Quantity')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{cm('purchaseItems.columns.unitPrice', 'Unit Price')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{cm('purchaseItems.columns.total', 'Total')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={`${item.referenceId || 'ref'}-${item.productId}-${index}`} hover>
                      <TableCell sx={{ color: 'text.secondary' }}>{index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{item.productName}</Typography>
                        {formatPurchasedAt(item.purchasedAt) && (
                          <Typography variant="caption" color="text.secondary">
                            {formatPurchasedAt(item.purchasedAt)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {item.totalQuantity} {item.unit}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{formatCurrency(item.unitPrice)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">{formatCurrency(item.totalAmount)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" color="text.secondary">{cm('purchaseItems.grandTotal', 'Grand Total:')}</Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.main">
                {formatCurrency(grandTotal)}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} variant="outlined">{cm('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PurchaseItemsModal;
